import React from "react"
import { Controller, UseFormWatch } from "react-hook-form"
import { Control, UseFormSetValue } from "react-hook-form/dist/types/form"
import {
  DiscountCodeRule,
  DiscountExternalRule,
  InputCreateOrUpdateDiscountCodeRule
} from "lib/types/generated/graphql-types"
import { FieldErrors } from "react-hook-form/dist/types/errors"
import { RULE_TYPE } from "lib/types/common"

import { CheckboxWrapper, Container, Name, SortOrder } from "./DiscountRuleGeneralInput.styled"

import Input from "components/Ui/Form/Input"
import Checkbox from "components/Ui/Form/Checkbox"
import DisplayNameInput from "./DisplayNames/DisplayNameInput"
import TimePeriodSelectionDiscountRules from "./TimePeriodSelectionDiscountRules"
import { WithAuth } from "../../Auth"
import { PermissionGroups } from "../../../routes/constants"
import FallbackInput from "../../Ui/Form/FallbackInput"
import FallbackCheckbox from "../../Ui/Form/FallbackCheckbox"

type Props = {
  discountRule: DiscountExternalRule | DiscountCodeRule
  control: Control<InputCreateOrUpdateDiscountCodeRule>
  isStackable: boolean
  errors: FieldErrors<InputCreateOrUpdateDiscountCodeRule>
  type: RULE_TYPE | undefined
  setValue: UseFormSetValue<InputCreateOrUpdateDiscountCodeRule>
  watch: UseFormWatch<InputCreateOrUpdateDiscountCodeRule>
}

const DiscountRuleGeneralUpdateInput = ({
  discountRule,
  control,
  isStackable,
  errors,
  setValue,
  watch,
  type
}: Props) => {
  const getTypeText = () => {
    switch (type) {
      case RULE_TYPE.CODE_RULE:
        return "discount rule with codes generated from Brink"
      case RULE_TYPE.EXTERNAL_RULE:
        return "discount rule with codes generated from external provider"
    }
  }

  if (!discountRule) return null

  return (
    <div>
      <Container>
        <div>
          <h2>General</h2>
          <p>General inputs for {getTypeText()}</p>
          <Name>
            <WithAuth
              requiredGroups={PermissionGroups.discounts.write}
              fallback={<FallbackInput label={"Name"} value={discountRule.name} />}
            >
              <Controller
                name="name"
                render={({ field }) => (
                  <Input
                    {...field}
                    type="text"
                    label="Name *"
                    placeholder="Aa - Zz, 0 - 9"
                    errors={errors}
                  />
                )}
                control={control}
                defaultValue={discountRule.name}
                rules={{
                  required: "This is a required field"
                }}
              />
            </WithAuth>
          </Name>
          <SortOrder>
            <WithAuth
              requiredGroups={PermissionGroups.discounts.write}
              fallback={
                <FallbackInput label={"Sort order"} value={discountRule.sortOrder.toString()} />
              }
            >
              <Controller
                name="sortOrder"
                render={({ field }) => (
                  <Input
                    {...field}
                    type="number"
                    label="Sort order *"
                    placeholder="Minimum value is 1"
                    errors={errors}
                  />
                )}
                control={control}
                defaultValue={discountRule.sortOrder}
                rules={{
                  required: "This is a required field",
                  min: { value: 1, message: "Minimum value is 1" }
                }}
              />
            </WithAuth>
          </SortOrder>
          <CheckboxWrapper>
            <WithAuth
              requiredGroups={PermissionGroups.discounts.write}
              fallback={<FallbackCheckbox label={"Active"} value={discountRule.isActive} />}
            >
              <Checkbox
                control={control}
                name="isActive"
                label="Active"
                defaultValue={discountRule.isActive}
              />
            </WithAuth>
            <WithAuth
              requiredGroups={PermissionGroups.discounts.write}
              fallback={
                <FallbackCheckbox
                  label={"Is stackable"}
                  value={discountRule.isStackable}
                  description={"Can this rule stack with other rules?"}
                />
              }
            >
              <Checkbox
                control={control}
                name="isStackable"
                label="Is stackable"
                description="Can this rule stack with other rules?"
                defaultValue={discountRule.isStackable}
              />
            </WithAuth>
            <WithAuth
              requiredGroups={PermissionGroups.discounts.write}
              fallback={
                <FallbackCheckbox
                  label={"Is exclusive"}
                  value={discountRule.isExclusive}
                  description={"Only one exclusive rule can be applied per cart."}
                />
              }
            >
              <Checkbox
                disabled={!isStackable}
                control={control}
                name="isExclusive"
                label="Is exclusive"
                description="Only one exclusive rule can be applied per cart."
                defaultValue={discountRule.isExclusive}
              />
            </WithAuth>
            <WithAuth
              requiredGroups={PermissionGroups.discounts.write}
              fallback={
                <FallbackCheckbox
                  label={"Apply last"}
                  value={discountRule.applyLast}
                  description={"Apply this discount rule last if multiple rules are applied."}
                />
              }
            >
              <Checkbox
                disabled={!isStackable}
                control={control}
                name="applyLast"
                label="Apply last"
                description="Apply this discount rule last if multiple rules are applied."
                defaultValue={discountRule.applyLast}
              />
            </WithAuth>
          </CheckboxWrapper>
        </div>
        <div>
          <DisplayNameInput
            setValue={setValue}
            watch={watch}
            savedDisplayNames={discountRule?.displayNames ?? []}
          />
        </div>
      </Container>
      <hr />
      <WithAuth requiredGroups={PermissionGroups.discounts.write} fallback={<FallbackInput label={"Time period limitation (Optional)"} dateTime={discountRule.validDateRange ?? undefined}/>}>
        <TimePeriodSelectionDiscountRules
          control={control}
          errors={errors}
          defaultValues={discountRule.validDateRange ?? undefined}
        />
      </WithAuth>
    </div>
  )
}

export default DiscountRuleGeneralUpdateInput
